import React, { useState } from "react";

import { ChevronDown, ChevronRight, Download, FileText } from "lucide-react";

import { Card } from "@/components/ui/Card";
import Skeleton from "@/components/shared/Skeleton";
import { Button } from "@/components/ui/Button";

// ── Shared chart palette ──────────────────────────────────────────────────────
// Raw-hex accents, matching the 2A dashboard (StatusDonut / ComplianceFunnel) so
// the analytics page reads as the same product. Structure (cards, text, borders)
// uses the project's `bg-background-*` / `text-text-*` / `border-border-primary`
// tokens; only the data marks carry these accents. Reused, ordered, never cycled.
export const CHART = {
  indigo: "#6366f1",
  blue: "#3b82f6",
  sky: "#0ea5e9",
  green: "#22c55e",
  amber: "#f59e0b",
  violet: "#a855f7",
  red: "#ef4444",
  slate: "#64748b",
};

// Ordered categorical series (assigned in fixed order, never cycled).
export const SERIES = [CHART.indigo, CHART.blue, CHART.sky, CHART.green, CHART.amber, CHART.violet];

// Status → color, shared with the dashboard's stat cards / donut.
//
// `notUploaded` is not a document status — it is a checklist slot with nothing
// in it — so it takes the neutral slate rather than a status accent. The
// candidate's Compliance tab made that call first (`bg-background-icon`, a
// neutral); this is the hex equivalent, so the chip and the bar agree.
//
// ⚠️ `dashboard/components/StatusDonut.jsx` restates these five as raw hex in
// its own `SLICES`. Two copies of one truth: change one and the donut and the
// bars disagree about the same tenant. Debt, not a pattern to extend.
export const STATUS_COLOR = {
  approved: CHART.green,
  rejected: CHART.red,
  reRequested: CHART.amber,
  pendingForReview: CHART.blue,
  pendingFromCandidate: CHART.violet,
  notUploaded: CHART.slate,
};

export const ANALYTICS_CSV_BUTTON =
  "h-7 px-2.5 rounded-lg text-xs font-medium border border-border-primary bg-background-tertiary text-text-primary hover:bg-background-icon hover:text-text-primary focus-visible:border-text-secondary focus-visible:ring-2 focus-visible:ring-text-secondary/50 inline-flex items-center justify-center gap-1.5 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

/**
 * The export pair every analytics section carries: the numbers, or the report.
 *
 * CSV is for someone who will re-sort and re-total in a spreadsheet; PDF is for
 * someone who will read it — charts included — or forward it to a manager or an
 * auditor. Both are GETs of data already on screen, so neither is ever disabled
 * for permission reasons; they disable only while the section is still loading,
 * because exporting a half-loaded view would download the wrong numbers.
 *
 * One component rather than a pair of buttons per section, so all five sections
 * keep the same affordance and a change to either lands everywhere at once.
 */
export const ExportButtons = ({ onExportCsv, onExportPdf, loading, busy }) => (
  <div className="flex items-center gap-1.5">
    <Button className={ANALYTICS_CSV_BUTTON} onClick={onExportCsv} disabled={loading || busy} title="Download the numbers as CSV">
      <Download size={13} className="shrink-0" />
      <span>CSV</span>
    </Button>
    <Button className={ANALYTICS_CSV_BUTTON} onClick={onExportPdf} disabled={loading || busy} title="Download this view as a PDF report, charts included">
      <FileText size={13} className="shrink-0" />
      <span>PDF</span>
    </Button>
  </div>
);

// A rate → color ramp for "higher is better" metrics (accuracy, compliance).
export const goodRateColor = (pct) => {
  if (pct >= 85) return CHART.green;
  if (pct >= 60) return CHART.amber;
  return CHART.red;
};

// A rate → color ramp for "lower is better" metrics (rework, breach, correction).
export const badRateColor = (pct) => {
  if (pct >= 40) return CHART.red;
  if (pct >= 15) return CHART.amber;
  return CHART.green;
};

// ── Section shell ─────────────────────────────────────────────────────────────
// One titled analytics family. Header carries an icon, title, optional subtitle
// (the leadership question it answers), and a right-aligned slot for the CSV
// export button. Body is whatever the family renders.
export const Section = ({ icon: Icon, title, subtitle, action, children }) => (
  <Card className="p-0 overflow-hidden flex flex-col">
    <div className="px-4 py-3 border-b border-border-primary flex items-center gap-2">
      {Icon ? <Icon size={15} className="text-text-primary/70 flex-shrink-0" /> : null}
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-text-primary leading-tight truncate">{title}</h3>
        {subtitle ? <p className="text-[11px] text-text-secondary leading-tight truncate">{subtitle}</p> : null}
      </div>
      {action ? <div className="flex-shrink-0">{action}</div> : null}
    </div>
    <div className="p-4">{children}</div>
  </Card>
);

// ── KPI tile ──────────────────────────────────────────────────────────────────
// A single headline number with a label + optional unit + optional accent color
// (left border). Used for the F1 / F4 hero bands.
export const KpiTile = ({ label, value, unit, color = CHART.indigo, loading, hint }) => (
  <div
    className="rounded-xl p-3.5 h-full flex flex-col justify-between bg-background-tertiary border border-border-primary"
    style={{ borderLeftWidth: 4, borderLeftColor: color }}
  >
    {loading ? (
      <Skeleton className="h-7 w-16" />
    ) : (
      <p className="text-[1.6rem] font-bold tracking-tight leading-none text-text-primary">
        {value ?? "—"}
        {unit ? <span className="text-[0.9rem] font-semibold text-text-secondary ml-0.5">{unit}</span> : null}
      </p>
    )}
    <div className="mt-2">
      <p className="text-[0.78rem] leading-tight text-text-secondary">{label}</p>
      {hint ? <p className="text-[0.68rem] leading-tight text-text-secondary mt-0.5">{hint}</p> : null}
    </div>
  </div>
);

// ── Horizontal bar row ──────────────────────────────────────────────────────────
// A labelled proportional bar (magnitude comparison / ranked lists). `value` is
// the number shown; `pct` is the fill width (0-100); `color` the fill. Matches
// the CSS-bar idiom used by the 2A funnel.
export const BarRow = ({ label, value, valueLabel, pct, color = CHART.indigo, sublabel }) => (
  <div className="flex items-center gap-3 py-1.5">
    <div className="w-[38%] min-w-0">
      <p className="text-[12px] text-text-primary truncate leading-tight">{label}</p>
      {sublabel ? <p className="text-[10px] text-text-secondary truncate leading-tight">{sublabel}</p> : null}
    </div>
    <div className="flex-1 h-5 rounded-md bg-background-tertiary overflow-hidden">
      <div className="h-full rounded-md transition-all duration-500" style={{ width: `${Math.max(2, Math.min(100, pct || 0))}%`, background: `${color}cc` }} />
    </div>
    <span className="text-[12px] font-semibold text-text-primary w-14 text-right shrink-0 tabular-nums">{valueLabel ?? value}</span>
  </div>
);

// ── Empty / loading helpers ─────────────────────────────────────────────────────
export const BarsSkeleton = ({ rows = 4 }) => (
  <div className="flex flex-col gap-2 py-1">
    {[...Array(rows)].map((_, i) => (
      <Skeleton key={i} className="h-6 w-full" />
    ))}
  </div>
);

export const EmptyNote = ({ children }) => <p className="text-[12px] text-text-secondary py-8 text-center">{children}</p>;

// Format a days value compactly (null → em dash).
export const fmtDays = (d) => (d === null || d === undefined ? "—" : `${d}d`);
export const fmtPct = (p) => (p === null || p === undefined ? "—" : `${p}%`);

// A "(n=X)" sample-size suffix, so every rate/ratio on the page shows its
// underlying count — added 2026-08-01 alongside the minimum-sample-size rule.
export const fmtN = (n) => (n === null || n === undefined ? "" : `n=${n}`);

// ── Below-minimum-sample badge ───────────────────────────────────────────────
// A small inline flag for any row excluded from "worst first"/outlier ranking
// because its n is under the tenant's minimum-sample-size floor. Still shown,
// just clearly marked — never silently hidden.
export const LowSampleBadge = ({ minSampleSize }) => (
  <span
    className="text-[9px] font-medium px-1 py-0.5 rounded border border-border-primary text-text-secondary whitespace-nowrap"
    title={`Below the minimum sample size (n<${minSampleSize ?? 10}) — shown, but excluded from ranking/outlier comparisons.`}
  >
    low n
  </span>
);

// ── Collapsible detail block ─────────────────────────────────────────────────
// Families 2–5 open on their headline KPIs + attention state; their detailed
// breakdown views (stage tables, per-reviewer tables, per-doc-type/field
// accuracy tables, per-slot quality tables) collapse by default and expand on
// click, so the page stays scannable top-to-bottom (added 2026-08-01).
export const Collapsible = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-3">
      <Button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[12px] font-medium text-text-primary py-1.5 w-full text-left hover:text-text-primary/80"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {title}
      </Button>
      {open ? <div className="mt-2">{children}</div> : null}
    </div>
  );
};
